import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Table } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { StatsCards } from '../../components/StatsCards';
import { FilterBar } from '../../components/FilterBar';
import { audioService, Audio } from '../../services/audioService';
import api from '../../config/api';

export const AdminMediatheque: React.FC = () => {
  const [allAudios, setAllAudios] = useState<Audio[]>([]);
  const [audios, setAudios] = useState<Audio[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState<Audio | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    total_ecoutes: 0,
    total_likes: 0,
    total_downloads: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    categorie: '',
  });

  useEffect(() => {
    fetchAudios(1);
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allAudios, currentPage]);

  const fetchStats = async () => {
    try {
      const allData = await audioService.getAll(1, 1000);
      const allAudios = allData.results || [];
      
      const statsData = {
        total: allData.count || allAudios.length,
        total_ecoutes: allAudios.reduce((sum: number, audio: Audio) => sum + (audio.nombre_ecoutes || 0), 0),
        total_likes: allAudios.reduce((sum: number, audio: Audio) => sum + (audio.nombre_likes || 0), 0),
        total_downloads: allAudios.reduce((sum: number, audio: Audio) => sum + (audio.nombre_downloads || 0), 0),
      };
      setStats(statsData);
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const fetchAudios = async (page: number = 1) => {
    setLoading(true);
    try {
      const data = await audioService.getAll(1, 1000);
      const allAudiosData = data.results || [];
      setAllAudios(allAudiosData);
      setTotalCount(allAudiosData.length);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allAudios];

    // Filtre par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((audio) =>
        audio.titre?.toLowerCase().includes(searchLower) ||
        audio.personne?.toLowerCase().includes(searchLower) ||
        audio.description?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par catégorie
    if (filters.categorie) {
      filtered = filtered.filter((audio) => audio.categorie === filters.categorie);
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || 0).getTime();
      const dateB = new Date((b as any).created_at || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const pageSize = 6;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedAudios = filtered.slice(startIndex, endIndex);

    setAudios(paginatedAudios);
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const handleCreate = () => {
    setFormData({});
    setSelectedAudio(null);
    setIsModalOpen(true);
  };

  const handleEdit = (audio: Audio) => {
    setFormData({
      titre: audio.titre,
      personne: audio.personne,
      categorie: audio.categorie,
      description: audio.description || '',
      duree_secondes: audio.duree_secondes || '',
      date_creation: audio.date_creation || '',
    });
    setSelectedAudio(audio);
    setIsModalOpen(true);
  };

  const handleView = (audio: Audio) => {
    setSelectedAudio(audio);
    setIsViewModalOpen(true);
  };

  const handleDelete = (audio: Audio) => {
    setSelectedAudio(audio);
    setIsDeleteModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          if (key === 'audio_file' && formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      if (selectedAudio) {
        await api.put(`/mediatheque/audios/${selectedAudio.id}/`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/mediatheque/audios/', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setIsModalOpen(false);
      await fetchAudios(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    }
  };

  const confirmDelete = async () => {
    if (!selectedAudio) return;
    try {
      await api.delete(`/mediatheque/audios/${selectedAudio.id}/`);
      setIsDeleteModalOpen(false);
      await fetchAudios(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    }
  };

  const getCategorieLabel = (cat: string) => {
    const labels: { [key: string]: string } = {
      bourde: '📖 Bourde',
      khilass: '✨ Khilass',
      gamou: '🎉 Gamou',
      dahira: '🤝 Dahira',
      coran: '📿 Coran',
    };
    return labels[cat] || cat;
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'titre', 
      label: 'Titre',
      render: (value: string) => <strong className="text-green-700">{value}</strong>
    },
    {
      key: 'personne',
      label: 'Personne',
      render: (value: string) => <span className="font-medium">{value}</span>
    },
    {
      key: 'categorie',
      label: 'Catégorie',
      render: (value: string) => (
        <span className="bg-pink-100 text-pink-800 px-2 py-1 rounded-full text-xs font-semibold">
          {getCategorieLabel(value)}
        </span>
      )
    },
    {
      key: 'duree_formatee',
      label: 'Durée',
      render: (value: string) => value || '-'
    },
    {
      key: 'nombre_ecoutes',
      label: 'Écoutes',
      render: (value: number) => (
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
          👁️ {value}
        </span>
      )
    },
  ];

  if (loading) {
    return (
      <AdminLayout activeSection="mediatheque">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="mediatheque">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-green-700">Gestion de la Médiathèque</h2>
          <p className="text-gray-600 mt-1">Créez et gérez tous les audios</p>
        </div>
        <button onClick={handleCreate} className="btn-primary text-lg px-6 py-3">
          + Nouvel Audio
        </button>
      </div>

      {/* Cartes de statistiques */}
      <StatsCards
        stats={[
          { label: 'Total Audios', value: stats.total, icon: '🎵', color: 'purple' },
          { label: 'Total Écoutes', value: stats.total_ecoutes, icon: '👁️', color: 'blue' },
          { label: 'Total Likes', value: stats.total_likes, icon: '❤️', color: 'red' },
          { label: 'Total Téléchargements', value: stats.total_downloads, icon: '📥', color: 'green' },
        ]}
      />

      {/* Filtres */}
      <FilterBar
        searchPlaceholder="🔍 Rechercher par titre, personne..."
        searchValue={filters.search}
        onSearchChange={(value) => {
          setFilters({ ...filters, search: value });
          setCurrentPage(1);
        }}
        filters={[
          {
            key: 'categorie',
            label: 'Toutes les catégories',
            value: filters.categorie,
            onChange: (value) => {
              setFilters({ ...filters, categorie: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'bourde', label: '📖 Bourde' },
              { value: 'khilass', label: '✨ Khilass' },
              { value: 'gamou', label: '🎉 Gamou' },
              { value: 'dahira', label: '🤝 Dahira' },
              { value: 'coran', label: '📿 Coran' },
              { value: 'autre', label: '📌 Autre' },
            ],
          },
        ]}
        onClear={() => {
          setFilters({ search: '', categorie: '' });
          setCurrentPage(1);
        }}
      />

      <Table
        columns={columns}
        data={audios}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        loading={loading}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedAudio ? 'Modifier l\'audio' : 'Nouvel audio'}
        size="lg"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={handleSubmit} className="btn-primary">
              Enregistrer
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
            <input
              type="text"
              className="input-field"
              value={formData.titre || ''}
              onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Personne *</label>
              <input
                type="text"
                className="input-field"
                value={formData.personne || ''}
                onChange={(e) => setFormData({ ...formData, personne: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie *</label>
              <select
                className="input-field"
                value={formData.categorie || ''}
                onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                required
              >
                <option value="">Sélectionner</option>
                <option value="bourde">📖 Bourde</option>
                <option value="khilass">✨ Khilass</option>
                <option value="gamou">🎉 Gamou</option>
                <option value="dahira">🤝 Dahira</option>
                <option value="coran">📿 Coran</option>
                <option value="autre">📌 Autre</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              className="input-field min-h-[100px]"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Durée (secondes)</label>
              <input
                type="number"
                className="input-field"
                value={formData.duree_secondes || ''}
                onChange={(e) => setFormData({ ...formData, duree_secondes: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de création</label>
              <input
                type="date"
                className="input-field"
                value={formData.date_creation || ''}
                onChange={(e) => setFormData({ ...formData, date_creation: e.target.value })}
              />
            </div>
          </div>

          {!selectedAudio && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fichier audio *</label>
              <input
                type="file"
                accept="audio/*"
                className="input-field"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setFormData({ ...formData, audio_file: e.target.files[0] });
                  }
                }}
                required={!selectedAudio}
              />
            </div>
          )}
        </form>
      </Modal>

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Détails de l'audio"
      >
        {selectedAudio && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Titre</p>
              <p className="font-semibold text-lg">{selectedAudio.titre}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Personne</p>
                <p className="font-semibold">{selectedAudio.personne}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Catégorie</p>
                <p className="font-semibold">{getCategorieLabel(selectedAudio.categorie)}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600">Statistiques</p>
              <div className="flex gap-4 mt-2">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">👁️ {selectedAudio.nombre_ecoutes}</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full">❤️ {selectedAudio.nombre_likes}</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">📥 {selectedAudio.nombre_downloads}</span>
              </div>
            </div>
            {selectedAudio.audio_file && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Écouter</p>
                <audio controls className="w-full">
                  <source src={selectedAudio.audio_file} />
                </audio>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmer la suppression"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsDeleteModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={confirmDelete} className="btn-danger">
              Supprimer
            </button>
          </div>
        }
      >
        <p className="text-lg">Êtes-vous sûr de vouloir supprimer l'audio <strong>"{selectedAudio?.titre}"</strong> ?</p>
      </Modal>
    </AdminLayout>
  );
};

